import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { RootStore } from '../../stores/RootStore';
import StoreContext from '../../stores/StoreContext';
import { HookTypes, withHooks } from '../../utils/withHooks';
import Footer from '../footer/Footer';
import Helmet from 'react-helmet';
import { Company } from '../../stores/company/Company';
import CompanyLanding from './CompanyLanding';
import { Box, Typography } from '@mui/material';

type Props = HookTypes;

const CompanyLandingScreen = observer(
	class CompanyLandingScreen extends React.Component<Props> {
		static readonly contextType = StoreContext;
		get params() {
			return this.props.params!;
		}

		loading: boolean = false;
		loadStart: number = Date.now();

		constructor(props: Props) {
			super(props);

			makeObservable(this, {
				loading: observable,
				loadStart: observable,
				rootStore: computed,
				companyStore: computed,
				uiState: computed,
				company: computed,
				workspaceName: computed,
				image: computed,
				loadTimedOut: computed,
				loadCompanyDetails: action,
			});
		}

		get rootStore() {
			return this.context as RootStore;
		}

		get companyStore() {
			return this.rootStore.companyStore;
		}

		get uiState() {
			return this.rootStore.uiState;
		}

		get workspaceStore() {
			return this.rootStore.workspaceStore;
		}

		get company(): Company | undefined {
			if (!this.workspaceName) {
				return undefined;
			}
			return this.companyStore.findCompanyByDomain(this.workspaceName);
		}

		get workspaceName() {
			return this.params.workspaceName;
		}

		get image() {
			const { workspaceTheme } = this.uiState;

			const imageNumber = Math.floor(Math.random() * 3) + 1;

			switch (imageNumber) {
				case 3:
					return `/illustrations/ads/${workspaceTheme}3.png`;
				case 2:
					return `/illustrations/ads/${workspaceTheme}2.png`;
				default:
					return `/illustrations/ads/${workspaceTheme}1.png`;
			}
		}

		get loadTimedOut() {
			return Date.now() - this.loadStart > 10000;
		}

		componentDidMount() {
			this.loadCompanyDetails().catch(console.warn);

			try {
				console.log('BefareAiChat', (window as any).BefareAiChat);
				if ((window as any).BefareAiChat) {
					(window as any).BefareAiChat.init(this.workspaceName);
				}
			} catch (error) {
				console.log('BefareAiChat', error);
			}
		}

		componentWillUnmount(): void {
			try {
				if ((window as any).BefareAiChat) {
					(window as any).BefareAiChat.destroy();
				}
			} catch (error) {
				console.log('BefareAiChat', error);
			}
		}

		componentDidUpdate(prevProps: Readonly<HookTypes>): void {
			if (this.workspaceName !== prevProps.params?.workspaceName && !this.loading) {
				this.loadCompanyDetails().catch(console.warn);
			}
		}

		loadCompanyDetails = async () => {
			console.log('loadCompanyDetails', this.workspaceName);
			if (!this.workspaceName) {
				return;
			}

			if (this.loading) {
				return;
			}
			this.loading = true;
			this.loadStart = Date.now();

			const metadata = await this.workspaceStore.getMetadataFromDomain(this.workspaceName).catch(console.error);
			if (metadata?.data?.id) {
				await this.companyStore.loadCompanyFromWorkspaceId(metadata.data.id).catch(console.error);
			}

			runInAction(() => {
				this.loading = false;
			});
		};

		render() {
			return (
				<>
					{this.company?.name && (
						<Helmet defer={false}>
							<title>{this.uiState.generateTitleString(undefined, this.company?.name)}</title>
							<meta
								property="og:title"
								content={this.uiState.generateTitleString(undefined, this.company?.name, false)}
							/>
							<meta
								property="og:description"
								content={this.uiState.generateDescriptionString(this.company?.description)}
							/>
							<meta property="og:locale" content="no_NO" />
							{this.company.logo && <meta property="og:image:alt" content={this.company.logo} />}
							<meta property="og:image" content={this.image} />
						</Helmet>
					)}

					{this.loadTimedOut && !this.loading && (
						<Box sx={{ margin: '4rem auto', maxWidth: 800, minHeight: '80vh', padding: 2 }}>
							<Typography gutterBottom variant="h4">
								404 - Ikke funnet
							</Typography>
							<Typography gutterBottom variant="body2">
								Her er det noe rusk i maskineriet. Vi finner ikke dette firmaet akkurat nå.
							</Typography>
							<Typography gutterBottom variant="body2">
								Du kan prøve å laste siden på nytt for å se om det hjelper.
							</Typography>
						</Box>
					)}

					{!this.company?.name && !this.loadTimedOut && (
						<Typography gutterBottom variant="body2">
							Laster informasjon om bedriften...
						</Typography>
					)}

					<CompanyLanding company={this.company} />

					<Footer />
				</>
			);
		}
	}
);

export default withHooks(CompanyLandingScreen);
